import { Blog } from '../../../@types/blog';
import { useEffect } from 'react';
import axiosInstance from '../../../utils/axios';
import { APIS } from '../../../utils/serviceUrls';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Autocomplete, Box, Chip, FormGroup, Stack, TextField } from '@mui/material';
import { FormProvider, RHFTextField, RHFTextarea } from '../../../components/hook-form';
import TextMaxLine from '../../../components/TextMaxLine';
import { UploadAvatar } from '../../../components/upload';
import { LoadingButton } from '@mui/lab';
import { BlogSchema } from './index';
import ReactQuill from 'react-quill';
import { formats } from '../../../components/editor/EditorToolbar';

type BlogNewEditFormProps = {
  isEdit: boolean;
  currentBlog?: Blog;
  handleClose(): void;
};

const BlogNewEditForm = ({ isEdit, currentBlog, handleClose }: BlogNewEditFormProps) => {
  const { defaultValues, NewBlogSchema } = BlogSchema(currentBlog);
  const { enqueueSnackbar } = useSnackbar();

  const methods = useForm<any>({
    resolver: yupResolver(NewBlogSchema),
    defaultValues,
  });

  const {
    reset,
    watch,
    setValue,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = methods;

  const values = watch();

  const onSubmit = async (data: any) => {
    if (!data.title_ar) delete data?.title_ar;
    if (!data.description_ar) delete data?.description_ar;
    if (!data.keywords_ar) delete data?.keywords_ar;
    try {
      if (currentBlog) await axiosInstance.patch(APIS.BLOGS.PATCH(currentBlog._id as string), data);
      else await axiosInstance.post(APIS.BLOGS.ADD, data);
      reset();
      enqueueSnackbar(!isEdit ? 'Create success!' : 'Update success!');
      handleClose();
    } catch (err) {
      if (err?.message) {
        enqueueSnackbar(err?.message, { variant: 'error' });
      }

      if (err?.messages?.length && err?.messages[0]) {
        enqueueSnackbar(err?.messages.length && err?.messages[0], { variant: 'error' });
      } else {
        enqueueSnackbar(!isEdit ? 'Unable to create Blog!' : 'Unable to edit Blog', {
          variant: 'error',
        });
      }
    }
  };

  const uploadImage = async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    const response = await axiosInstance.post(APIS.UPLOAD.MEDIA, form);
    return response.data;
  };

  useEffect(() => {
    console.log('Errorrs', errors);
  }, [errors]);
  return (
    <Stack height={'100%'} style={{ overflow: 'auto' }}>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <TextMaxLine marginBottom={'0px'} marginTop={'0px'} fontSize={'22px'}>
          {isEdit ? ' Edit Blog' : ' Create Blog'}
        </TextMaxLine>
        <Stack direction={'column'} justifyContent={'space-between'} height={'auto'}>
          <Stack spacing={3}>
            <Stack direction={'row'} justifyContent={'center'} spacing={9}>
              <Stack direction={'column'}>
                <p
                  style={{
                    color: '#FF4842',
                    width: '100%',
                    textAlign: 'center',
                    fontSize: '0.75rem',
                    marginTop: '10px',
                  }}
                >
                  {errors?.icon && String(errors?.icon?.message)}
                </p>
              </Stack>
              <Stack>
                <UploadAvatar
                  label="Blog Image"
                  accept={{ 'image/*': [] }}
                  error={!!errors.image}
                  file={values.image}
                  onDropAccepted={async (file) => {
                    let response = await uploadImage(file[0]);
                    setValue('image', response?.url);
                  }}
                />
                <p
                  style={{
                    color: '#FF4842',
                    width: '100%',
                    textAlign: 'center',
                    fontSize: '0.75rem',
                    marginTop: '10px',
                  }}
                >
                  {errors?.image && String(errors?.image?.message)}
                </p>
              </Stack>
            </Stack>

            <Box
              sx={{
                display: 'grid',
                columnGap: 1,
                rowGap: 1,
                // gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              <RHFTextField name="title" label="English Blog Title" />
              <RHFTextField name="title_ar" label="Arabic Blog Title" />
            </Box>
            <FormGroup
              sx={{
                display: 'grid',
                columnGap: 1,
                rowGap: 1,
                // gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              <RHFTextField
                multiline
                minRows={2}
                name="description"
                maxRows={3}
                label="English Description"
              />
              <RHFTextField
                multiline
                minRows={2}
                name="description_ar"
                maxRows={3}
                label="Arabic Description"
              />

              <RHFTextField
                multiline
                minRows={2}
                name="seoDescription"
                maxRows={3}
                label="English SEO-Description"
              />
              <RHFTextField
                multiline
                minRows={2}
                name="seoDescription_ar"
                maxRows={3}
                label="Arabic SEO-Description"
              />
            </FormGroup>
            <FormGroup
              sx={{
                display: 'grid',
                gap: 8,
              }}
            >
              <RHFTextarea name="content" placeholderText="Write your content in english..." />
              <RHFTextarea name="content_ar" placeholderText="Write your content in arabic..." />
            </FormGroup>
            <div style={{ marginTop: 60 }}>
              <Autocomplete
                multiple
                id="keywords"
                options={[]}
                value={values.keywords}
                freeSolo
                renderTags={(value: string[], getTagProps) =>
                  value.map((option: string, index: number) => (
                    <Chip
                      key={index}
                      variant="outlined"
                      label={option}
                      onDelete={(e) => {
                        values.keywords.splice(index, 1);

                        setValue('keywords', values.keywords);
                      }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    error={values.keywords.length === 0 && errors.keywords ? true : false}
                    {...params}
                    name={'keywords'}
                    onKeyDown={(e) => {
                      let event = e as any;
                      if (e.key === 'Enter') {
                        setValue('keywords', [...values.keywords, event?.target?.value as any]);
                      }
                      if (e.key === 'Backspace') {
                        const value = values?.keywords;
                        value?.pop();

                        setValue('keywords', value);
                      }
                    }}
                    variant="outlined"
                    label="Keywords"
                    placeholder="Keywords"
                    maxRows={3}
                  />
                )}
              />
              {values.keywords.length === 0 && (
                <span style={{ color: '#FF4842', fontSize: '0.75rem', marginTop: '5px' }}>
                  {errors?.keywords?.message as any}
                </span>
              )}
            </div>
            <div>
              <Autocomplete
                multiple
                id="keywords_ar"
                options={[]}
                value={values?.keywords_ar ?? []}
                freeSolo
                renderTags={(value: string[], getTagProps) =>
                  value.map((option: string, index: number) => (
                    <Chip
                      key={index}
                      variant="outlined"
                      label={option}
                      onDelete={(e) => {
                        values?.keywords_ar.splice(index, 1);

                        setValue('keywords_ar', values?.keywords_ar);
                      }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name={'keywords_ar'}
                    onKeyDown={(e) => {
                      let event = e as any;
                      if (e.key === 'Enter') {
                        setValue(
                          'keywords_ar',
                          values?.keywords_ar
                            ? [...values?.keywords_ar, event?.target?.value as any]
                            : [event?.target?.value as any]
                        );
                      }
                      if (e.key === 'Backspace') {
                        const value = values?.keywords_ar;
                        value?.pop();

                        setValue('keywords_ar', value);
                      }
                    }}
                    variant="outlined"
                    label="Arabic Keywords"
                    placeholder="Keywords"
                    maxRows={3}
                  />
                )}
              />
            </div>
          </Stack>
          <Stack alignItems="flex-end" sx={{ mt: 3 }}>
            <LoadingButton
              style={{ width: '200px', height: '50px' }}
              type="submit"
              variant="contained"
              loading={isSubmitting}
            >
              {!isEdit ? 'Create Blog' : 'Save Changes'}
            </LoadingButton>
          </Stack>
        </Stack>
      </FormProvider>
    </Stack>
  );
};
export default BlogNewEditForm;
